import type {
  CompanyNameSuggestion,
  CompanyProfile,
  ContextFile,
  ContextInsight,
  LangCode,
  Meeting,
  MeetingStage,
} from "@scoach/types";
import { create } from "zustand";

export interface AutoFillSuggestion {
  fromMeetingId: string;
  clientName: string;
  meetingType: string;
  date: string;
  stage: MeetingStage;
  goal: string;
  language: "auto" | LangCode;
  website: string;
}

const STAGE_ORDER: MeetingStage[] = ["Intro", "Discovery", "Qualification", "Negotiation"];

function nextStage(current: MeetingStage): MeetingStage {
  const idx = STAGE_ORDER.indexOf(current);
  return idx >= 0 && idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1]! : current;
}

export interface PreMeetingState {
  hydrated: boolean;

  client: string;
  website: string;
  title: string;
  stage: MeetingStage;

  goal: string;
  language: "auto" | LangCode;
  /** Language for Sally's on-screen hints/answers; "auto" follows `language`. */
  assistanceLanguage: "auto" | LangCode;
  hintTone: "direct" | "consultative" | "brief";

  contextFiles: ContextFile[];
  insights: ContextInsight | null;
  analyzing: boolean;

  priorMeetings: Meeting[];
  autoFillSuggestion: AutoFillSuggestion | null;
  autoFillDismissed: boolean;

  companySuggestions: CompanyNameSuggestion[];
  companyProfile: CompanyProfile | null;
  researchingCompany: boolean;

  patchFields: (
    patch: Partial<
      Pick<
        PreMeetingState,
        | "client"
        | "website"
        | "title"
        | "stage"
        | "goal"
        | "language"
        | "assistanceLanguage"
        | "hintTone"
      >
    >,
  ) => void;
  setContextFiles: (files: ContextFile[]) => void;
  setInsights: (i: ContextInsight | null) => void;
  setAnalyzing: (a: boolean) => void;
  setPriorMeetings: (meetings: Meeting[]) => void;
  applyAutoFill: () => void;
  dismissAutoFill: () => void;
  setCompanySuggestions: (s: CompanyNameSuggestion[]) => void;
  setCompanyProfile: (p: CompanyProfile | null) => void;
  setResearchingCompany: (r: boolean) => void;
  hydrateFromMeeting: (m: Meeting) => void;
  reset: () => void;
}

type Actions =
  | "patchFields"
  | "setContextFiles"
  | "setInsights"
  | "setAnalyzing"
  | "setPriorMeetings"
  | "applyAutoFill"
  | "dismissAutoFill"
  | "setCompanySuggestions"
  | "setCompanyProfile"
  | "setResearchingCompany"
  | "hydrateFromMeeting"
  | "reset";

const initial: Omit<PreMeetingState, Actions> = {
  hydrated: false,
  client: "",
  website: "",
  title: "",
  stage: "Discovery",
  goal: "",
  language: "auto",
  assistanceLanguage: "auto",
  hintTone: "consultative",
  contextFiles: [],
  insights: null,
  analyzing: false,
  priorMeetings: [],
  autoFillSuggestion: null,
  autoFillDismissed: false,
  companySuggestions: [],
  companyProfile: null,
  researchingCompany: false,
};

function buildSuggestion(meetings: Meeting[], clientName: string): AutoFillSuggestion | null {
  const needle = clientName.toLowerCase().trim();
  if (!needle) return null;
  const match = meetings
    .filter((m) => m.account?.name?.toLowerCase().includes(needle))
    .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())[0];
  if (!match) return null;
  return {
    fromMeetingId: match.id,
    clientName: match.account.name,
    meetingType: match.meetingType ?? match.stage,
    date: match.createdAt
      ? new Date(match.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : "",
    stage: nextStage(match.stage),
    goal: match.goal ?? "",
    language: match.language ?? "auto",
    website: match.account.website ?? "",
  };
}

export const usePreMeetingStore = create<PreMeetingState>()((set, get) => ({
  ...initial,
  patchFields: (patch) => {
    set(patch);
    if (patch.client !== undefined) {
      // The previously-researched profile is for the old name — drop it.
      if (get().companyProfile) set({ companyProfile: null });
      const { priorMeetings, autoFillDismissed } = get();
      if (!autoFillDismissed && priorMeetings.length > 0) {
        set({ autoFillSuggestion: buildSuggestion(priorMeetings, patch.client) });
      }
    }
  },
  setContextFiles: (contextFiles) => set({ contextFiles }),
  setInsights: (insights) => set({ insights }),
  setAnalyzing: (analyzing) => set({ analyzing }),
  setPriorMeetings: (meetings) => {
    set({ priorMeetings: meetings });
    const { client, autoFillDismissed } = get();
    if (client && !autoFillDismissed) {
      set({ autoFillSuggestion: buildSuggestion(meetings, client) });
    }
  },
  applyAutoFill: () => {
    const { autoFillSuggestion } = get();
    if (!autoFillSuggestion) return;
    set({
      stage: autoFillSuggestion.stage,
      goal: autoFillSuggestion.goal,
      language: autoFillSuggestion.language,
      website: autoFillSuggestion.website,
    });
  },
  dismissAutoFill: () => set({ autoFillDismissed: true, autoFillSuggestion: null }),
  setCompanySuggestions: (companySuggestions) => set({ companySuggestions }),
  setCompanyProfile: (companyProfile) => set({ companyProfile }),
  setResearchingCompany: (researchingCompany) => set({ researchingCompany }),
  hydrateFromMeeting: (m) => {
    set({
      hydrated: true,
      client: m.account?.name ?? "",
      website: m.account?.website ?? "",
      title: m.title ?? "",
      stage: m.stage ?? "Discovery",
      goal: m.goal ?? "",
      language: m.language ?? "auto",
      assistanceLanguage: m.assistanceLanguage ?? "auto",
      hintTone: "consultative",
      contextFiles: m.contextFiles ?? [],
      insights: null,
    });
  },
  reset: () => set(initial),
}));
